import {ReactElement} from "react";
import {PD_LG} from "../../shared/dimens";
import {colorBlue, lightMediumGray} from "../../shared/colors";
import {ButtonBase, Grid, Typography} from "@mui/material";
import {Book, Page, PageElement, PageElementType, PageLayoutBlockType} from "../types";
import useResizeObserver from "use-resize-observer";
import {BaseApp} from "../../shared/BaseApp";
import {EditTextFragment} from "@/smilebook-shared/components/EditTextFragment";

function PageBlockView(props: {
  thumbnail?: boolean,
  book?: Book,
  page: Page,
  blockIndex: number,
  selectedElement?: PageElement,
  setSelectedElement?: (element: PageElement) => void,
  selectedImage: string,
  setSelectedImage: (image: string) => void,
}): ReactElement {
  const blockIndex = props.blockIndex;
  const spec = props.page.layout.blockSpecs()[blockIndex];
  if (spec.type === PageLayoutBlockType.NONE) {
    return null;
  }
  const block = props.page.layout.block(blockIndex);
  const mediaImageId = block?.mediaId;
  const imageAttributes = block?.imageAttributes;
  const textAttributes = block?.textAttributes;
  const mediaImage = mediaImageId ? props.book?.media.images.find(image => image.id === mediaImageId) : null;
  let hasText: boolean = false;
  switch (spec.type) {
    case PageLayoutBlockType.IMAGE_SUBTEXT:
      hasText = true;
      break;
  }
  const maxWidth = 720 * (hasText && props.page.layout.cols() === 1 ? .75 : 1);
  return <ButtonBase
    style={{position: "relative"}}
    disabled={props.thumbnail}
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      props.setSelectedElement({page: props.page, type: PageElementType.IMAGE, blockIndex: props.blockIndex});
      if (!props.selectedImage) {
        return;
      }
      block.mediaId = props.selectedImage;
      props.setSelectedImage?.(null);
    }}>
    <div style={{
      width: maxWidth * spec.imageAspectRatio / props.page.layout.cols(),
      aspectRatio: spec.imageAspectRatio,
      background: lightMediumGray,
      overflow: "hidden",
      border: !props.thumbnail
        ? (props.selectedElement?.type === PageElementType.IMAGE && props.selectedElement?.blockIndex === props.blockIndex ? "8px solid " + colorBlue : "2px dashed black")
        : null,
    }}>
      {mediaImage
        ? <img src={mediaImage.url} style={{
          transform: `scale(${imageAttributes.scale}, ${imageAttributes.scale})`,
          verticalAlign: "top",
          width: "100%",
          height: "100%",
          objectFit: "cover"
        }}/>
        : null}
    </div>
    {hasText
      ? <ButtonBase
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          props.setSelectedElement({page: props.page, type: PageElementType.SUBTEXT, blockIndex: props.blockIndex});
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          props.setSelectedElement({page: props.page, type: PageElementType.SUBTEXT, blockIndex: props.blockIndex});
          const id = BaseApp.CONTEXT.showDialog(null, () => <EditTextFragment value={block.subtext} setValue={text => {
            block.subtext = text;
            BaseApp.CONTEXT.hideDialog(id);
          }} onCancel={() => {
            BaseApp.CONTEXT.hideDialog(id);
          }}/>);
        }}
        style={{
          border: !props.thumbnail
            ? ((props.selectedElement?.type === PageElementType.TEXT || props.selectedElement?.type === PageElementType.SUBTEXT) && props.selectedElement?.blockIndex === props.blockIndex ? "8px solid " + colorBlue : "2px dashed black")
            : null,
          left: 0,
          width: "100%",
          height: 72,
          bottom: -80,
          position: "absolute",
        }}>
        <Typography
          style={{
            width: "100%",
            height: "100%",
            textAlign: textAttributes?.textAlign,
            color: textAttributes?.color,
            fontFamily: textAttributes?.fontFamily,
            fontSize: textAttributes?.fontSize,
          }}>
          {block.subtext}
        </Typography>
      </ButtonBase>
      : null}
  </ButtonBase>;
}

export function PageView(props: {
  thumbnail?: boolean,
  book?: Book,
  page: Page,
  zoom?: number,
  selectedElement?: PageElement,
  setSelectedElement?: (element: PageElement) => void,
  selectedImage?: string,
  setSelectedImage?: (image: string) => void,
  style?: any,
}): ReactElement {
  const {ref, width = 1} = useResizeObserver<HTMLDivElement>();
  return <div ref={ref}
              style={{display: "flex", flexGrow: 1, width: "100%", alignItems: "center", justifyContent: "center"}}>
    <ButtonBase
      onClick={e => {
        e.preventDefault();
        props.setSelectedElement?.({page: props.page, type: PageElementType.BACKGROUND});
      }}
      style={{
        zoom: width * (props.thumbnail ? 1 : props.zoom || 1) / 1200,
        width: 1200,
        aspectRatio: 3 / 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: PD_LG,
        background: props.page.background,
        ...props.style,
      }}>
      <Grid container>
        {props.page.layout.blockSpecs().map((spec, index) => <Grid xs={12 / props.page.layout.cols()}>
          <div style={{width: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
            <PageBlockView
              thumbnail={props.thumbnail}
              book={props.book}
              page={props.page}
              blockIndex={index}
              selectedElement={props.selectedElement}
              setSelectedElement={props.setSelectedElement}
              selectedImage={props.selectedImage}
              setSelectedImage={props.setSelectedImage}/>
          </div>
        </Grid>)}
      </Grid>
    </ButtonBase>
  </div>;
}